import { httpClientV2, logger } from ".";
import { getLeadSessionId } from "@/utils/leadTracking";
import type { Variant } from "./compareTypes";
import type { CityData } from "@/types/city";
import { bannerDimensions, DETECT_LOCATION_API } from "./constants";
import {
    BodyType,
    FetchBrandsResponse,
    FetchModelsResponse,
    FetchLatestUpdatesResponse,
    GetModelPriceExtremesParams,
    LeadFormData,
    ModelData,
    ModelPriceExtremeResponse,
    NewsItem,
    TopBrands,
    FetchVariantsResponse,
    Transmission,
    SeatingCapacity,
    GetSeatingCapacityListParams,
    FetchSeatingCapacityListResponse
} from "./interfaces";
import { getSessionId } from "./session";
import type {
    GetModelColorsByModelsResponse,
    GetModelColorsParams,
    GetModelColorsResponse,
} from "./types";
import { BMS_CONFIG } from "@/config/bms.config";
import { unstable_cache } from "next/cache";
import { getLocationCookie } from "./locationCookie";

const LM_BASE_URL = process.env.NEXT_PUBLIC_LM_API_URL ?? "";

async function lmFetch(path: string) {
    const res = await fetch(`${LM_BASE_URL}${path}`, {
        headers: { "Content-Type": "application/json" },
        next: { revalidate: 300 },
    });
    if (!res.ok) throw new Error(`LM API error: ${res.status} ${path}`);
    return res.json();
}

// Fetch brands with pagination
// Fetch brands with pagination (accepts options object)
export const getBrands = async (
    opts: { page?: number; limit?: number; sort?: "asc" | "desc"; is_popular?: boolean; is_active?: boolean, priority_order?: string[], min_price?: number, max_price?: number, isUpcoming?: boolean, fuel_type?: string, seating_capacity?: number | string, news_brand?: boolean } = {}
): Promise<FetchBrandsResponse> => {
    try {
        const { page = 1, limit = 10, sort = "asc", is_popular = false, is_active, priority_order, min_price, max_price, isUpcoming, fuel_type, seating_capacity, news_brand = false } = opts;
        const params = new URLSearchParams({ page: String(page), limit: String(limit), sort });
        if (is_popular) params.set("is_popular", "true");
        if (typeof is_active !== "undefined") params.set("is_active", String(Boolean(is_active)));
        if (priority_order && Array.isArray(priority_order)) {
            params.append("priority_order", JSON.stringify(priority_order));
        }
        if (min_price) params.set("min_price", String(min_price));
        if (max_price) params.set("max_price", String(max_price));
        if (fuel_type) params.set("fuel_type", String(fuel_type));
        if (seating_capacity) params.set("seating_capacity", String(seating_capacity));
        if (isUpcoming) params.set("isUpcoming", String(isUpcoming));
        if (news_brand) params.set("news_brand", "true");

        const url = `brand/all?${params.toString()}`;
        const response = await httpClientV2(url);
        return response as FetchBrandsResponse;
    } catch (error) {
        logger.error("Error fetching brands:", error);
        return { status: "error", data: [], totalCount: 0 } as FetchBrandsResponse;
    }
};

export const getBrandBySlug = async (
    opts: { slug: string } = { slug: '' }
) => {
    try {
        const { slug } = opts;

        if (!slug) {
            throw new Error("slug is required to fetch brand");
        }

        const url = `brand/single/${slug.toString()}`;
        const response = await httpClientV2(url);
        return response;
    } catch (error) {
        logger.error("Error fetching brands:", error);
        return { status: "error", data: [], totalCount: 0 };
    }
};

interface GetModelsParams {
    brand?: string | string[];         // brand slug or ID (e.g., "maruti" or ObjectId)
    page?: number;
    limit?: number;
    sort?: "asc" | "desc" | "price_asc" | "price_desc" | "discontinued_desc" | 'discontinued_asc' | 'launch_date_asc' | 'launch_date_desc' | 'sort_order_asc' | 'sort_order_desc';             // price low-to-high or high-to-low
    search?: string;                   // text search query for model name
    fuel_type?: string | string[];     // e.g., "Petrol", "Electric", ["Petrol", "CNG"]
    body_type?: string | string[];
    transmission?: string | string[];
    drivetrain?: string | string[];
    seating_capacity?: string | number | (string | number)[];
    is_popular?: boolean;
    is_latest?: boolean;
    is_upcoming?: boolean;
    is_trending?: boolean;
    min_price?: number;
    max_price?: number;
    launch_date?: string;
    is_new_launch?: boolean;
    mileage?: string | string[];
    range?: string | string[];
    battery_capacity?: string | string[];
    is_discontinued?: boolean;
    exclude_ids?: string | string[];
}

// Use `FetchModelsResponse` imported from ./interfaces — do not redeclare here.

// V2 API uses price_range: { min, max } but the entire frontend expects { base, top }.
// Normalize at the API boundary so no component needs to change.
function normalizePriceRange(m: any): any {
    if (!m || typeof m !== 'object') return m;
    if (m.price_range && typeof m.price_range === 'object') {
        const pr = m.price_range;
        if (pr.min !== undefined && pr.base === undefined) pr.base = pr.min;
        if (pr.max !== undefined && pr.top === undefined) pr.top = pr.max;
    }
    return m;
}

// Flexible client-side helper
export const getModels = async (options: GetModelsParams = {}): Promise<FetchModelsResponse> => {
    try {
        const {
            brand,
            page = 1,
            limit = 10,
            sort = "sort_order_asc",
            search,
            fuel_type,
            body_type,
            transmission,
            drivetrain,
            seating_capacity,
            is_popular = false,
            is_latest = false,
            is_upcoming = undefined,
            is_trending = false,
            min_price,
            max_price,
            launch_date,
            is_new_launch = false,
            mileage,
            range,
            battery_capacity,
            is_discontinued = false,
            exclude_ids
        } = options;

        // Build URL: /model/all or /model/all/{brand}
        const url = "model/all";
        // if (brand) url += `/${brand}`;

        const params = new URLSearchParams({
            page: String(page),
            limit: String(Math.min(limit, 100)), // respect server max
        });

        if (min_price) {
            params.set("min_price", String(min_price));
        }

        if (max_price) {
            params.set("max_price", String(max_price));
        }

        if (launch_date) {
            params.set("launch_date", String(launch_date));
        }

        if (search) params.set("search", String(search));

        // Only add if explicitly one of the backend's known sort modes
        if (sort === "asc" || sort === "desc" || sort === 'price_desc' || sort === 'price_asc' || sort === 'discontinued_desc' || sort === 'discontinued_asc' || sort === 'launch_date_asc' || sort === 'launch_date_desc' || sort === 'sort_order_asc' || sort === 'sort_order_desc') {
            params.set("sort", sort);
        }
        // Otherwise: backend defaults to newest first (createdAt desc)

        // Boolean flags
        if (is_popular) params.set("is_popular", "true");
        if (is_latest) params.set("is_latest", "true");
        if (is_trending) params.set("is_trending", "true");

        if (is_upcoming === true) {
            params.set("status", "upcoming");
        } else if (is_upcoming === false) {
            params.set("status", "active");
        } else if (is_discontinued === true) {
            params.set("status", "discontinued");
        }

        if (is_new_launch) params.set("is_new_launch", "true");

        // Multi-value filters (support string or array)
        const addParam = (key: string, value: string | string[]) => {
            if (!value) return;
            const values = Array.isArray(value) ? value : [value];
            values.forEach((v) => params.append(key, v.toString().trim()));
        };

        addParam("brand", brand as any);
        addParam("fuel_type", fuel_type as any);
        addParam("body_type", body_type as any);
        addParam("transmission", transmission as any);
        addParam("drivetrain", drivetrain as any);
        addParam("seating_capacity", seating_capacity as any);
        addParam("mileage", mileage as any);
        addParam("range", range as any);
        addParam("battery_capacity", battery_capacity as any);
        addParam("exclude_ids", exclude_ids as any);

        const response: any = await httpClientV2(`${url}?${params.toString()}`);
        if (Array.isArray(response?.data)) response.data = response.data.map(normalizePriceRange);

        // Backend (cj-nestjs-backend) nests pagination as { data, pagination: { total, page, totalPages } }
        // (or { count, data } when limit=-1). Flatten to the flat shape the rest of the app expects.
        const pg = response?.pagination || {};
        const numericLimit = Math.min(limit, 100);
        const totalCount = response?.totalCount ?? pg.total ?? response?.count ?? (response?.data?.length ?? 0);
        const totalPages = response?.totalPages ?? pg.totalPages ?? (numericLimit > 0 ? Math.ceil(totalCount / numericLimit) : 1);
        return {
            ...response,
            page: response?.page ?? pg.page ?? page,
            totalPages,
            totalCount,
        } as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching models:", error);
        return {
            status: "error",
            page: 1,
            totalPages: 0,
            totalCount: 0,
            data: [],
        } as FetchModelsResponse;
    }
};

export interface GetModelCountsParams {
    brand?: string | string[];
    body_type?: string | string[];
    fuel_type?: string | string[];
}

export interface ModelCounts {
    active: number;
    upcoming: number;
    discontinued: number;
    /** active + upcoming — everything you can buy now or soon, discontinued excluded */
    available: number;
    /** active + upcoming + discontinued */
    total: number;
}

// Fetches active/upcoming/discontinued counts separately (status accepts one value per request) and combines them.
export const getModelCounts = async (
    filters: GetModelCountsParams = {}
): Promise<ModelCounts> => {
    const [activeRes, upcomingRes, discontinuedRes] = await Promise.all([
        getModels({ ...filters, limit: 1, is_upcoming: false }),
        getModels({ ...filters, limit: 1, is_upcoming: true }),
        getModels({ ...filters, limit: 1, is_discontinued: true }),
    ]);

    const active = activeRes?.totalCount ?? 0;
    const upcoming = upcomingRes?.totalCount ?? 0;
    const discontinued = discontinuedRes?.totalCount ?? 0;

    return {
        active,
        upcoming,
        discontinued,
        available: active + upcoming,
        total: active + upcoming + discontinued,
    };
};

// Fetch model by ID
export const getModelByID = async (
    id: string
): Promise<FetchModelsResponse> => {
    try {
        const safeId = encodeURIComponent(String(id));
        const url = `model/single/${safeId}`;
        const response = await httpClientV2(url);
        return response as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching model by ID:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};

export const getModelImageGallery = async (modelId: string) => {
    try {
        const safeId = encodeURIComponent(String(modelId));
        const response = await httpClientV2(`model-image-gallery/${safeId}`);
        return response?.data ?? null;
    } catch (error) {
        logger.error("Error fetching model image gallery:", error);
        return null;
    }
};

// Fetch models by brand ID
export interface GetModelsByBrandIDParams {
    brandId: string;
    page?: number;
    limit?: number;
    sort?: "asc" | "desc" | "sort_order_asc" | "sort_order_desc";
    is_top_variant?: string;
    is_discontinued?: boolean;
    is_upcoming?: boolean;
}

export const getModelsByBrandID = async (
    paramsObj: GetModelsByBrandIDParams
): Promise<FetchModelsResponse> => {
    try {
        const { brandId, page = 1, limit = 10, sort = "sort_order_asc", is_discontinued, is_upcoming } = paramsObj;

        const params = new URLSearchParams({ page: String(page), limit: String(limit), sort });
        if (brandId) params.set("brand_id", brandId);
        if (is_upcoming === true) {
            params.set("status", "upcoming");
        } else if (is_discontinued === true) {
            params.set("status", "discontinued");
        } else {
            params.set("status", "active");
        }

        const response = await httpClientV2(`model/all?${params.toString()}`);
        if (Array.isArray(response?.data)) response.data = response.data.map(normalizePriceRange);
        return response as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching models by brand:", error);
        return { status: "error", data: [], totalPages: 1, page: 1, totalCount: 0 } as any;
    }
};

// Get model detail by brand slug and model slug
export const getModelByBrandAndSlug = async (
    brand: string,
    model: string,
    options?: { page_type?: "pdp" | "emi" | "price"; location_id?: string }
): Promise<FetchModelsResponse> => {
    try {
        // Handle edge case where model is null or with any extension i.e "null.html" or "null.aspx"
        if (!model || model.match(/\.\w+$/) || model === "null") return {
            status: "error",
            page: 1,
            totalPages: 0,
            totalCount: 0,
            data: [],
        } as FetchModelsResponse;

        // Prefer the `cars/single/byslug/:model` endpoint which returns
        // the model details by slug (used elsewhere in the app).
        // Fall back to a `detail/{brand}/{model}` path if needed.
        const safeModel = encodeURIComponent(String(model));
        const safeBrand = encodeURIComponent(String(brand));

        // Try the canonical slug endpoint first
        let url = `model/detail/${safeBrand}/${safeModel}?includeContent=true`;
        if (options?.page_type) {
            url += `&page_type=${encodeURIComponent(options.page_type)}`;
        }
        if (options?.location_id) {
            url += `&location_id=${encodeURIComponent(options.location_id)}`;
        }
        const response = await httpClientV2(url);
        if (response?.data) response.data = normalizePriceRange(response.data);
        return response as FetchModelsResponse;

    } catch (error) {
        logger.error("Error fetching cars:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};

export const getModelColors = async ({
    modelId,
    variantId,
    isPromoted,
}: GetModelColorsParams): Promise<GetModelColorsResponse> => {
    try {
        if (!modelId) {
            return { status: "error", success: false, count: 0, data: [] };
        }

        const params = new URLSearchParams();
        if (variantId) params.set("variant_id", String(variantId));
        if (isPromoted !== undefined && isPromoted !== null && isPromoted !== "") {
            params.set("is_promoted", String(isPromoted));
        }

        const query = params.toString();
        const url = `model-color/${encodeURIComponent(String(modelId))}${query ? `?${query}` : ""}`;
        const response = await httpClientV2(url);
        const data = Array.isArray(response?.data) ? response.data : [];
        return {
            ...response,
            data,
            count: Number(response?.count ?? data.length) || data.length,
        } as GetModelColorsResponse;
    } catch (error) {
        logger.error("Error fetching model colors:", error);
        return { status: "error", success: false, count: 0, data: [] };
    }
};

export const getModelColorsByModels = async (
    modelIds: string[],
): Promise<GetModelColorsByModelsResponse> => {
    try {
        const ids = modelIds.filter(Boolean);
        if (!ids.length) {
            return { status: "error", success: false, data: {} };
        }

        const response = await httpClientV2("model-color/by-models", {
            method: "POST",
            body: { model_ids: ids },
        });
        const data =
            response?.data && typeof response.data === "object" && !Array.isArray(response.data)
                ? response.data
                : {};
        return { ...response, data } as GetModelColorsByModelsResponse;
    } catch (error) {
        logger.error("Error fetching model colors by models:", error);
        return { status: "error", success: false, data: {} };
    }
};

// Fetch a page of a model's "recently updated" changelog (newest first, active only).
// Used by the model page's Load More button — the first page ships embedded in
// getModelByBrandAndSlug's response, this backs subsequent pages.
export const getModelLatestUpdates = async (
    modelId: string,
    page: number = 1,
    limit: number = 5
): Promise<FetchLatestUpdatesResponse> => {
    try {
        const safeId = encodeURIComponent(String(modelId));
        const response = await httpClientV2(`model/${safeId}/latest-updates?page=${page}&limit=${limit}`);
        return response as FetchLatestUpdatesResponse;
    } catch (error) {
        logger.error("Error fetching model latest updates:", error);
        return { data: [], pagination: { total: 0, page, limit, totalPages: 0 }, status: "error" };
    }
};

// Fetch similar models by model id
export const getSimilarModels = async (id: string, limit: number = 12, pageType?: string): Promise<FetchModelsResponse> => {
    try {
        // return { status: "error", data: [], totalPages: 1, page: 1 } as any;
        const safeId = encodeURIComponent(String(id));
        let url = `model/similar/${safeId}?limit=${limit}`;
        if (pageType) {
            url += `&page_type=${pageType}`;
        }
        const response = await httpClientV2(url);

        // V2 returns flat model docs; reshape to { brand, model, price_range } expected by SimilarCarsSlider
        const raw: any[] = (response?.data ?? []).map(normalizePriceRange);
        const data = raw.map((m: any) => ({
            brand: {
                _id: m.brand_id,
                name: m.brand_name,
                slug: m.brand_slug,
            },
            model: {
                _id: m._id,
                name: m.name,
                slug: m.slug,
                detailed_slug: m.detailed_slug,
                image: m.featured_image,
                is_upcoming: m.status === 'upcoming',
                launch_date: m.launch_date,
                status: m.status ?? (m.is_upcoming ? 'upcoming' : 'active'),
                fuel_types: m.fuel_types ?? (m.fuel_type ? [m.fuel_type] : []),
                isEV: Array.isArray(m.fuel_types)
                    ? m.fuel_types.some((f: string) => String(f).toLowerCase() === 'electric')
                    : String(m.fuel_type || "").toLowerCase() === 'electric',
                hasMileageRange: (m.listing_summary ?? m.listing_summery)?.overall?.lowestMileage?.mileage != null,
                listing_summary: m.listing_summary ?? m.listing_summery,
            },
            price_range: m.price_range ?? { base: null, top: null },
        }));

        return { ...response, data } as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching similar models:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};

// Fetch recommended models by variant slug
export const getRelatedCarsByVariant = async (
    variantId: string,
    sort?: 'brand' | 'price_asc' | 'price_desc',
    includeBodyType?: boolean
): Promise<FetchModelsResponse> => {
    try {
        const safeId = encodeURIComponent(String(variantId));
        const params = new URLSearchParams();
        if (sort) params.set("sort", sort);
        if (includeBodyType) params.set("includeBodyType", "true");

        const queryString = params.toString();
        const url = `model/get-related-cars-by-variant/${safeId}${queryString ? `?${queryString}` : ""}`;

        const response = await httpClientV2(url);
        return response as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching recommended models by variant:", error);
        return { status: "error", data: [] } as any;
    }
};

// Fetch recommended models by brand + model slug
export const getRecommendedModels = async (brandSlug: string, modelSlug: string): Promise<FetchModelsResponse> => {
    try {
        const url = `model/recommended/${encodeURIComponent(brandSlug)}/${encodeURIComponent(modelSlug)}`;
        const response = await httpClientV2(url);
        return response as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching recommended models:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};

// Fetch admin-curated promoted models (already in display order)
export const getPromotedModels = async (): Promise<FetchModelsResponse> => {
    try {
        const response = await httpClientV2("model/promoted");
        return response as FetchModelsResponse;
    } catch (error) {
        logger.error("Error fetching promoted models:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};


// Fetch variant list by model ID
export const getVariantList = async (
    model: string,
    page: number,
    limit: number,
    sort: "asc" | "desc" = "asc",
    is_upcoming?: boolean,
): Promise<FetchVariantsResponse> => {
    try {
        let url = `variants/by-model/${model}?page=${page}&limit=${limit}&sort=${sort}&includeSpecs=true`;
        if (is_upcoming === true) url += `&status=upcoming`;
        else if (is_upcoming === false) url += `&status=active`;
        const response = await httpClientV2(url);
        return response;
    } catch (error) {
        logger.error("Error fetching cars:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};


// Fetch variant list by model ID
export const getVariantListOnly = async (
    model: string,
    page: number,
    limit: number,
    sort: "asc" | "desc" = "asc",
    is_discontinued: boolean | undefined = undefined,
): Promise<FetchModelsResponse> => {
    try {
        let url = `variants/by-model/${model}?page=${page}&limit=${limit}&sort=${sort}`;
        if (is_discontinued === true) {
            url += `&status=discontinued`;
        } else if (is_discontinued === false) {
            url += `&status=active`;
        }
        const response = await httpClientV2(url);
        return response;
    } catch (error) {
        logger.error("Error fetching cars:", error);
        return { status: "error", data: [], totalPages: 1, page: 1 } as any;
    }
};

// Fetch a single variant by brand/model/variant slug segments
export async function getVariant(
    brand: string,
    model: string,
    variant: string
): Promise<Variant | null> {
    const enc = encodeURIComponent;
    const path = `variants/${enc(brand)}/${enc(model)}/${enc(variant)}`;

    if (!variant || variant.match(/\.\w+$/) || variant === "null") return null;

    try {
        const response = await httpClientV2(path);
        if (!response) return null;
        let data: any = response?.data ?? response;

        // Handle common response shapes
        if (Array.isArray(data)) data = data[0] ?? null;
        if (data && typeof data === "object" && "data" in data) {
            data = data.data;
            if (Array.isArray(data)) data = data[0] ?? null;
        }

        return data as Variant | null;
    } catch (err) {
        logger.error("getVariant failed:", path, err);
        return null;
    }
}

// Fetch a single variant by brand/model/variant slug segments
export async function getVariantByDetailedSlugs(
    detailedSlug: string,
    variant: string = ""
): Promise<Variant | null> {
    const enc = encodeURIComponent;

    let path = `variants/by-model-detailed-slug/${enc(detailedSlug)}`;
    if (variant) {
        path += `/${enc(variant)}`;
    }

    try {
        const response = await httpClientV2(path);
        if (!response) return null;
        let data: any = response?.data ?? response;

        // Handle common response shapes
        if (Array.isArray(data)) data = data[0] ?? null;
        if (data && typeof data === "object" && "data" in data) {
            data = data.data;
            if (Array.isArray(data)) data = data[0] ?? null;
        }

        return data as Variant | null;
    } catch (err) {
        logger.error("getVariant failed:", path, err);
        return null;
    }
}


// Fetch detailed variant info by ID

// create-lead's DTO validates with forbidNonWhitelisted — callers (Splashpopup,
// ThanksPopup) attach extra display/tracking-only fields (brand, modelName,
// onRoadPrice, interested_car_ids, ...) to the same object passed to submitLead,
// so only these DTO-recognized fields may reach the request body.
const LEAD_DTO_FIELDS = [
    'car_id', 'variant_id', 'name', 'phone', 'email', 'location', 'locationData',
    'utm_campaign', 'utm_medium', 'utm_term', 'utm_source', 'utm_content',
    'current_url', 'page_type', 'client_session_id', 'lead_source',
] as const;

// Submit Lead
export async function submitLead(
    leadData: LeadFormData & { carId?: string | null; car_id?: string | null; variantId?: string | null; variant_id?: string | null }
) {
    try {
        const currentUrl = (leadData as any).current_url || (typeof window !== "undefined" ? window.location.href : undefined);
        const merged: Record<string, any> = {
            ...leadData,
            // accept either `carId` (camelCase) or `car_id` (snake_case)
            car_id: (leadData as any).carId ?? (leadData as any).car_id ?? null,
            variant_id: (leadData as any).variant_id ?? (leadData as any).variantId ?? null,
            current_url: currentUrl,
            // Lets the backend recognize "this browser tab already completed
            // OTP verification once" and skip asking again for other vehicles.
            client_session_id: getLeadSessionId(),
        };

        const body: Record<string, any> = {};
        for (const key of LEAD_DTO_FIELDS) {
            if (merged[key] !== undefined && merged[key] !== null) {
                body[key] = merged[key];
            }
        }

        const url = 'general/create-lead';
        const res = await httpClientV2(url, {
            method: "POST",
            body,
        });

        return res;
    } catch (error) {
        logger.error("Error in submitLead:", error);
        throw error;
    }
}

export interface LeadFormConfig {
    email_required: boolean;
    dealer_selection_enabled: boolean;
}

// Per-model lead form config — whether the form must collect email and
// whether a nearby-dealer step follows a confirmed lead (both set via model
// flags in admin). Fails open to "off" so a config outage never blocks lead
// submission.
export async function getLeadFormConfig(carId: string): Promise<LeadFormConfig> {
    try {
        const res = await httpClientV2(`general/lead-form-config?car_id=${encodeURIComponent(carId)}`, { method: "GET" });
        const ok = res?.status === "success";
        return {
            email_required: ok && !!res?.data?.email_required,
            dealer_selection_enabled: ok && !!res?.data?.dealer_selection_enabled,
        };
    } catch (error) {
        logger.error("Error in getLeadFormConfig:", error);
        return { email_required: false, dealer_selection_enabled: false };
    }
}

export interface ConsentConfig {
    is_displayed: boolean;
    default_checked: boolean;
    // Placeholders in braces are policy links, e.g.
    // "I agree to the {Privacy Policy} and {Terms & Conditions}."
    display_text: string;
}

// Used when the consent config can't be fetched: always ask for consent.
export const FALLBACK_CONSENT_CONFIG: ConsentConfig = {
    is_displayed: true,
    default_checked: false,
    // Mirrors the text DPDP currently serves for CJ.
    display_text: "By proceeding, you confirm and agree to {Terms & Conditions} and {Privacy Policy}.",
};

// DPDP consent checkbox config for lead forms (proxied + cached by the backend).
export async function getConsentConfig(): Promise<ConsentConfig> {
    try {
        const res = await httpClientV2("general/consent/document-config", { method: "GET" });
        const data = res?.status === "success" ? res?.data : null;
        if (!data || typeof data.display_text !== "string") return FALLBACK_CONSENT_CONFIG;
        return {
            is_displayed: !!data.is_displayed,
            default_checked: !!data.default_checked,
            display_text: data.display_text,
        };
    } catch (error) {
        logger.error("Error in getConsentConfig:", error);
        return FALLBACK_CONSENT_CONFIG;
    }
}

export type ConsentDocumentType = "PRIVACY_POLICY" | "TERMS_AND_CONDITIONS";

// DPDP may send a full HTML document; only its <body> can be injected into a
// page (a nested <head> could leak <style>/<title> into the whole site).
function extractBodyHtml(html: string): string {
    const body = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (body) return body[1].trim();
    return html
        .replace(/<!doctype[^>]*>/gi, "")
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "")
        .replace(/<\/?(html|body)[^>]*>/gi, "")
        .trim();
}

// Published Privacy Policy / T&C from the DPDP consent service, as injectable
// body HTML. Null on any failure so pages can fall back to their bundled copy.
export async function getConsentDocumentHtml(documentType: ConsentDocumentType): Promise<string | null> {
    try {
        const res = await httpClientV2(
            `general/consent/document-content?document_type=${documentType}`,
            { method: "GET" }
        );
        const content = res?.status === "success" ? res?.data?.document?.content : null;
        return typeof content === "string" ? extractBodyHtml(content) || null : null;
    } catch (error) {
        logger.error("Error in getConsentDocumentHtml:", error);
        return null;
    }
}

export interface LeadDealer {
    _id: string;
    name: string;
    address?: string;
    city_slug?: string;
    distance_km: number;
}

// Dealers near a confirmed lead's location, nearest first. Empty on any error
// so the lead flow can simply skip the dealer step.
export async function getLeadDealers(leadId: string): Promise<LeadDealer[]> {
    try {
        const res = await httpClientV2(`general/lead-dealers?lead_id=${encodeURIComponent(leadId)}`, { method: "GET" });
        return res?.status === "success" && Array.isArray(res?.data) ? res.data : [];
    } catch (error) {
        logger.error("Error in getLeadDealers:", error);
        return [];
    }
}

// Save the dealers the user picked for a lead
export async function submitLeadDealers(leadId: string, dealerIds: string[]) {
    try {
        return await httpClientV2('general/lead-dealers', {
            method: "POST",
            body: { lead_id: leadId, dealer_ids: dealerIds },
        });
    } catch (error) {
        logger.error("Error in submitLeadDealers:", error);
        return { status: "error", data: null };
    }
}

// Verify OTP for a lead created against an OTP-gated model (see submitLead)
export async function verifyLeadOtp(leadId: string, otp: string) {
    try {
        const url = 'general/verify-lead-otp';
        const res = await httpClientV2(url, {
            method: "POST",
            body: { lead_id: leadId, otp },
        });
        return res;
    } catch (error) {
        logger.error("Error in verifyLeadOtp:", error);
        throw error;
    }
}

// Resend OTP for a lead created against an OTP-gated model
export async function resendLeadOtp(leadId: string) {
    try {
        const url = 'general/resend-lead-otp';
        const res = await httpClientV2(url, {
            method: "POST",
            body: { lead_id: leadId },
        });
        return res;
    } catch (error) {
        logger.error("Error in resendLeadOtp:", error);
        throw error;
    }
}

// Submit support Lead
export async function submitSupportLead(leadData: any) {
    try {
        const currentUrl = leadData?.current_url || (typeof window !== "undefined" ? window.location.href : undefined);
        const body = {
            ...leadData,
            current_url: currentUrl,
        };

        const url = 'general/contact-support';
        const res = await httpClientV2(url, {
            method: "POST",
            body,
        });

        return res;
    } catch (error) {
        logger.error("Error in submitLead:", error);
        throw error;
    }
}

// Homepage Popular

export async function fetchSectionData(sectionType: string): Promise<{ cars: any[]; error: string | null }> {
    try {
        // Map sectionType to listing_type expected by getModels
        const map: Record<string, string> = {
            POPULAR: "is_popular",
            ELECTRIC: "is_electric",
            LATEST: "is_latest",
        };

        const listingFlag = map[sectionType] ?? undefined;

        // Build options for getModels and map listing flags to params
        const opts: GetModelsParams = { page: 1, limit: 10, sort: "asc" };
        if (listingFlag === "is_popular") opts.is_popular = true;
        else if (listingFlag === "is_latest") opts.is_latest = true;
        else if (listingFlag === "is_electric") opts.fuel_type = "Electric";

        const resp = await getModels(opts);
        const data = (resp && (resp.data || resp)) || [];
        return { cars: data, error: null };
    } catch (err: unknown) {
        logger.error("Error in fetchSectionData:", err);
        const error = err instanceof Error ? err.message : "Failed to load models. Please try again later.";
        return { cars: [], error };
    }
}


// Products
export async function fetchBrandModels(
    model: string
): Promise<{ modelData: ModelData[]; error: string | null }> {
    try {
        const url = `cars/single/byslug/${model}`;
        const fetchedData = await httpClientV2(url);
        const data = fetchedData.data || fetchedData;

        return { modelData: data, error: null };
    } catch (err: unknown) {
        logger.error("Error in fetchBrandModels:", err);
        const error =
            err instanceof Error
                ? err.message
                : "Failed to load cars. Please try again later.";
        return { modelData: [], error };
    }
}

// Fetch all news API
export async function fetchAllNews(
    page: number = 1,
    limit: number = 9,
    date_filter: string = '',
    search: string = '',
    content_type: string = '',
    tagIds: string = '', // Comma-separated brand/model/variant ids to filter by.
): Promise<{ news: any[]; totalPages: number; totalCount: number; error: string | null }> {
    try {
        let url = `news/all?page=${page}&limit=${limit}&status=Published&sort=desc&sortBy=published_at`;

        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }

        if (date_filter) {
            url += `&date_filter=${date_filter}`;
        }

        if (content_type) {
            url += `&content_type=${encodeURIComponent(content_type)}`;
        }

        if (tagIds) {
            url += `&tag_ids=${encodeURIComponent(tagIds)}`;
        }

        const response = await httpClientV2(url);

        // Handle different response formats
        if (response && typeof response === 'object') {
            // v2 returns pagination nested: { data, pagination: { totalPages, total } }
            const pg = (response as any).pagination || {};
            const totalCount = response.totalCount ?? pg.total ?? response.count ?? (Array.isArray(response.data) ? response.data.length : 0);
            const totalPages = response.totalPages ?? pg.totalPages ?? (limit > 0 && totalCount > 0 ? Math.ceil(totalCount / limit) : 1);

            if (response.status === 'success' && response.data) {
                return {
                    news: Array.isArray(response.data) ? response.data : [response.data],
                    totalPages: totalPages,
                    totalCount: totalCount,
                    error: null
                };
            }
            // If it has a data property with array and pagination info
            else if (response.data && Array.isArray(response.data)) {
                return {
                    news: response.data,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    error: null
                };
            }
            // If it's an array of objects
            else if (Array.isArray(response)) {
                return {
                    news: response,
                    totalPages: limit > 0 ? Math.ceil(response.length / limit) : 1,
                    totalCount: response.length,
                    error: null
                };
            }
        }

        // Default empty result
        return {
            news: [],
            totalPages: 1,
            totalCount: 0,
            error: null
        };
    } catch (err: unknown) {
        logger.error('Error fetching all news:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch news';
        return { news: [], totalPages: 1, totalCount: 0, error };
    }
}

// News search API with pagination support
export async function searchNews(
    title: string = "",
    page: number = 1,
    limit: number = 9
): Promise<{ news: any[]; totalPages: number; totalCount: number; error: string | null }> {
    try {
        const url = `news/search?title=${encodeURIComponent(title)}&page=${page}&limit=${limit}`;

        const response = await httpClientV2(url);

        // Handle the response format from your example
        if (response && typeof response === 'object') {
            // If it's a single object (like your example), put it in an array
            if (response._id && response.title) {
                return {
                    news: [response],
                    totalPages: 1,
                    totalCount: 1,
                    error: null
                };
            }
            // If it's an array of objects
            else if (Array.isArray(response)) {
                return {
                    news: response,
                    totalPages: Math.ceil(response.length / limit),
                    totalCount: response.length,
                    error: null
                };
            }
            // If it has a data property with array and pagination info
            else if (response.data && Array.isArray(response.data)) {
                return {
                    news: response.data,
                    totalPages: response.totalPages || Math.ceil((response.totalCount || response.data.length) / limit),
                    totalCount: response.totalCount || response.count || response.data.length,
                    error: null
                };
            }
            // If response has pagination structure
            else if (response.status === 'success' && response.data) {
                return {
                    news: Array.isArray(response.data) ? response.data : [response.data],
                    totalPages: response.totalPages || 1,
                    totalCount: response.totalCount || response.count || 1,
                    error: null
                };
            }
        }

        // Default empty result
        return {
            news: [],
            totalPages: 1,
            totalCount: 0,
            error: null
        };
    } catch (err: unknown) {
        logger.error('Error searching news:', err);
        const error = err instanceof Error ? err.message : 'Failed to search news';
        return { news: [], totalPages: 1, totalCount: 0, error };
    }
}

export interface NewsAutocompleteItem {
    id: string; // Brand or model id.
    type: "model" | "brand" | string;
    name: string;
    slug: string; // Used for building URLs only.
    brandSlug?: string; // Used for building URLs only.
    tag_ids?: string[]; // Ids to send as `tag_ids` to the news APIs.
}

// News autocomplete API
export async function fetchNewsAutocomplete(
    keyword: string,
    limit: number = 10
): Promise<{ success: boolean; data: NewsAutocompleteItem[]; error: string | null }> {
    if (!keyword || keyword.trim().length <= 2) {
        return { success: true, data: [], error: null };
    }
    try {
        const url = `news/autocomplete?keyword=${encodeURIComponent(keyword.trim())}&limit=${limit}`;
        const response = await httpClientV2(url);

        if (response && (response.success || response.status === 'success') && Array.isArray(response.data)) {
            return {
                success: true,
                data: response.data,
                error: null,
            };
        }
        return { success: false, data: [], error: null };
    } catch (err: unknown) {
        logger.error('Error fetching news autocomplete:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch autocomplete suggestions';
        return { success: false, data: [], error };
    }
}

// Fetch all tags API - Extract tags from news/all API
export async function fetchAllTags(): Promise<{ tags: string[]; error: string | null }> {
    try {
        const url = 'news/all';
        const response = await httpClientV2(url);

        // Extract unique tags from news data
        if (response) {
            let newsData: any[] = [];

            // Handle different response formats to get news array
            if (Array.isArray(response)) {
                newsData = response;
            }
            else if (response.data && Array.isArray(response.data)) {
                newsData = response.data;
            }
            else if (response.status === 'success' && response.data) {
                newsData = Array.isArray(response.data) ? response.data : [response.data];
            }

            // Extract unique tags — v2 schema uses news_tags, v1 used tags
            const uniqueTags = new Set<string>();
            newsData.forEach((article) => {
                const tags = article.news_tags ?? article.tags;
                if (tags && Array.isArray(tags)) {
                    tags.forEach((tag: string) => {
                        if (tag && tag.trim()) {
                            uniqueTags.add(tag.trim());
                        }
                    });
                }
            });

            const tags = Array.from(uniqueTags);
            return {
                tags: ['All', ...tags], // Add 'All' as first option
                error: null
            };
        }

        return {
            tags: ['All'],
            error: null
        };
    } catch (err: unknown) {
        logger.error("Error in fetchAllTags:", err);
        const error = err instanceof Error ? err.message : 'Failed to fetch tags';
        return { tags: ['All'], error };
    }
}

// Extract categories from news data since categories API doesn't exist
export function extractCategoriesFromNews(newsData: any[]): any[] {
    const uniqueTags = new Set<string>();

    newsData.forEach((article) => {
        if (article.tags && Array.isArray(article.tags)) {
            article.tags.forEach((tag: string) => {
                if (tag && tag.trim()) {
                    uniqueTags.add(tag.trim());
                }
            });
        }
    });

    return Array.from(uniqueTags).map(tag => ({
        name: tag,
        _id: tag.toLowerCase().replace(/\s+/g, '-')
    }));
}

// Fetch latest news API.
// tagIds: brand/model/variant _ids (string or list; comma-joined) — news is
// matched on the article's car-tag ids, so it survives slug renames.
export async function fetchLatestNews(
    limit: number = 4,
    tagIds?: string | string[]
): Promise<{ news: any[]; isFallback?: boolean; error: string | null }> {
    try {
        const ids = (Array.isArray(tagIds) ? tagIds : String(tagIds ?? '').split(','))
            .map((id) => String(id ?? '').trim())
            .filter(Boolean)
            .join(',');
        const url = `news/latest?limit=${limit}${ids ? `&tag_ids=${encodeURIComponent(ids)}` : ''}`;
        const response = await httpClientV2(url);

        if (response && typeof response === 'object') {
            if (response.status === 'success' && response.data) {
                return {
                    news: Array.isArray(response.data) ? response.data : [response.data],
                    isFallback: response.isFallback,
                    error: null
                };
            } else if (Array.isArray(response)) {
                return { news: response, error: null };
            } else if (response.data && Array.isArray(response.data)) {
                return { news: response.data, isFallback: response.isFallback, error: null };
            }
        }

        return { news: [], error: null };
    } catch (err: unknown) {
        logger.error('Error fetching latest news:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch latest news';
        return { news: [], error };
    }
}

// Fetch similar news API
export async function fetchSimilarNews(
    slug: string,
    limit: number = 4
): Promise<{ news: any[]; error: string | null }> {
    try {
        const url = `news/similar/${slug}?limit=${limit}`;
        const response = await httpClientV2(url);

        if (response && typeof response === 'object') {
            if (response.status === 'success' && response.data) {
                return {
                    news: Array.isArray(response.data) ? response.data : [response.data],
                    error: null
                };
            } else if (Array.isArray(response)) {
                return { news: response, error: null };
            } else if (response.data && Array.isArray(response.data)) {
                return { news: response.data, error: null };
            }
        }

        return { news: [], error: null };
    } catch (err: unknown) {
        logger.error('Error fetching similar news:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch similar news';
        return { news: [], error };
    }
}

// Fetch single news by slug API
export async function fetchNewsBySlug(
    slug: string
): Promise<{ news: any | null; error: string | null }> {
    try {
        const url = `news/single/${slug}`;
        const response = await httpClientV2(url);

        if (response && typeof response === 'object') {
            if (response.status === 'success' && response.data) {
                return { news: response.data, error: null };
            } else if (response.data) {
                return { news: response.data, error: null };
            }
        }

        return { news: null, error: 'News not found' };
    } catch (err: unknown) {
        logger.error('Error fetching news by slug:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch news';
        return { news: null, error };
    }
}

// Fetch news by tags API
export async function fetchNewsByTag(
    slug: string,
    type: string
): Promise<{ news: any | null; error: string | null }> {
    try {
        const url = `news/single/${slug}`;
        const response = await httpClientV2(url);

        if (response && typeof response === 'object') {
            if (response.status === 'success' && response.data) {
                return { news: response.data, error: null };
            } else if (response.data) {
                return { news: response.data, error: null };
            }
        }

        return { news: null, error: 'News not found' };
    } catch (err: unknown) {
        logger.error('Error fetching news by slug:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch news';
        return { news: null, error };
    }
}

// Detect Location APi

export async function detectLocation(
    onSuccess: (location: string) => void,
    onError: (message: string) => void
) {
    if (!navigator.geolocation) {
        onError("Geolocation is not supported by this browser.");
        return;
    }

    navigator.geolocation.getCurrentPosition(
        async (position) => {
            const { latitude, longitude } = position.coords;
            try {
                const response = await fetch(
                    `${DETECT_LOCATION_API}?latitude=${latitude}&longitude=${longitude}`
                );
                if (!response.ok) throw new Error("Failed to fetch location");

                const data = await response.json();
                const city = data.locality || data.city || "Unknown City";
                const state = data.principalSubdivision || "Unknown State";
                onSuccess(`${city}, ${state}`);
            } catch (err) {
                logger.error("Reverse geocoding error", err);
                onError("Unable to determine city and state. Please enter manually.");
                onSuccess(`${latitude}, ${longitude}`);
            }
        },
        () => onError("Unable to detect location. Please enter manually.")
    );
}

// send otp api
export async function sendOtp(mobile: string) {
    try {
        const url = 'auth/send-otp';
        const res = await httpClientV2(url, {
            method: "POST",
            body: { phone: mobile },
        });
        return res;
    } catch (error) {
        logger.error("Error in sendOtp:", error);
        throw error;
    }
}

export async function verifyOtp(mobile: string, otp: string, name?: string, profession?: string) {
    try {
        const url = 'auth/verify-otp';
        const res = await httpClientV2(url, {
            method: "POST",
            body: { phone: mobile, otp: otp, name: name, profession: profession },
        });
        return res;
    } catch (error) {
        logger.error("Error in verifyOtp:", error);
        throw error;
    }
}

export async function profleUpdate(name: string, profession: string) {
    try {
        const url = 'auth/profile-update';
        const token = typeof document !== "undefined"
            ? document.cookie.match(/(?:^|; )token=([^;]*)/)?.[1]
            : undefined;
        const res = await httpClientV2(url, {
            method: "POST",
            body: { name, profession },
            headers: token ? { Authorization: `Bearer ${decodeURIComponent(token)}` } : undefined,
        });
        return res;
    } catch (error) {
        logger.error("Error in profile Update:", error);
        throw error;
    }
}

// get meta data by page slug and type
export async function getMetaDataByPageSlug(
    pageSlug: string,
    pageType: string
): Promise<{ meta: any; error: string | null }> {
    try {
        const url = `general/get-meta-by-pageslug?type=${pageType}&page_slug=${pageSlug}`;
        const response = await httpClientV2(url);
        if (response && typeof response === 'object') {
            if (response.status === 'success' && response.data) {
                return { meta: response.data, error: null };
            } else if (response.data) {
                return { meta: response.data, error: null };
            }
        }
        return { meta: null, error: 'Meta data not found' };
    } catch (err: unknown) {
        logger.error('Error fetching meta data:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch meta data';
        return { meta: null, error };
    }
}

export interface ComparisonListQueryParams {
    page?: number;
    brand?: string;
    body_type?: string;
    fuel_type?: string;
    transmission?: string;
    seating_capacity?: string;
    min_price?: number;
    max_price?: number;
}

// Fetch popular comparisons
export async function getPopularComparisons(
    limit: number = 3,
    tags?: string,
    isPopular?: boolean,
    comparisonModelId?: string,
    filters?: ComparisonListQueryParams
) {
    try {
        let url = `comparison/all?page=${filters?.page ?? 1}&limit=${limit}`;
        if (tags) {
            url += `&tags=${encodeURIComponent(tags)}`;
        }
        if (isPopular) {
            url += `&isPopular=true`;
        }
        if (comparisonModelId) {
            url += `&comparisonModelId=${encodeURIComponent(comparisonModelId)}`;
        }
        if (filters) {
            Object.entries(filters).forEach(([key, val]) => {
                if (val !== undefined && val !== null && key !== 'page') {
                    url += `&${key}=${encodeURIComponent(String(val))}`;
                }
            });
        }
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (e) {
        logger.error("Error in getPopularComparisons:", e);
        return [];
    }
}

// Fetch All Brands
export async function fetchAllBrands(limit?: number): Promise<{
    brands: TopBrands[];
    error: string | null;
}> {
    try {
        const url = limit ? `brand/all?limit=${limit}` : 'brand/all';
        const fetchedData = await httpClientV2(url);
        const data = fetchedData?.data || fetchedData;
        if (!Array.isArray(data)) {
            return { brands: [], error: fetchedData?.message || "Failed to load brands. Please try again later." };
        }
        return { brands: data, error: null };
    } catch (err: unknown) {
        logger.error("Error in fetchAllBrands:", err);
        const error =
            err instanceof Error
                ? err.message
                : "Failed to load brands. Please try again later.";
        return { brands: [], error };
    }
}

// Fetch popular car news
export const fetchPopularCarNews = async (
    limit = 5,
    category?: string
): Promise<{
    news: NewsItem[];
    error: string | null;
}> => {
    try {
        const url = `news/popular?limit=${limit}${category ? `&category=${category}` : ''}`;
        const fetchedData = await httpClientV2(url);
        const data = fetchedData.data || fetchedData;
        return { news: data, error: null };

    } catch (err: unknown) {
        logger.error("Error in fetchPopularCarNews:", err);
        const error =
            err instanceof Error
                ? err.message
                : "Failed to load popular car news. Please try again later.";
        return { news: [], error };
    }
};

export async function getRecommendedCarsBySlugs(slugs: string[]) {
    try {
        // Backend defaults limit to 4 when omitted, silently dropping any
        // slugs past the 4th.
        const limit = Math.min(Math.max(slugs.length, 1), 20);
        const url = `model/compare?slugs=${slugs.join(",")}&limit=${limit}`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getRecommendedCarsBySlugs:", err);
        throw err;
    }
};

export async function getBodyTypeBySlug(slug: string) {
    try {
        const url = `body-type/detail/${encodeURIComponent(slug)}`
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getBodyTypeBySlug:", err);
        return [];
    }
};

export async function getFuelTypesByBrand(brandSlug: string) {
    try {
        const res = await httpClientV2(`model/filters?brand=${encodeURIComponent(brandSlug)}&status=active`);
        return res?.data?.fuel_types || [];
    } catch (err) {
        logger.error("Error fetching fuel types:", err);
        return [];
    }
}

export async function getTransmissionsByBrand(brandSlug: string) {
    try {
        const res = await httpClientV2(`model/filters?brand=${encodeURIComponent(brandSlug)}`);
        return res?.data?.transmissions || [];
    } catch (err) {
        logger.error("Error fetching transmissions:", err);
        return [];
    }
}

export async function getBodyTypesByBrand(brandSlug: string) {
    try {
        const res = await httpClientV2(`model/filters?brand=${encodeURIComponent(brandSlug)}`);
        return res?.data?.body_types || [];
    } catch (err) {
        logger.error("Error fetching body types:", err);
        return [];
    }
}

export async function getPriceRangeByBrand(brandSlug: string) {
    try {
        const res = await httpClientV2(`model/filters?brand=${encodeURIComponent(brandSlug)}`);
        return res?.data?.price_range || null;
    } catch (err) {
        logger.error("Error fetching price range:", err);
        return null;
    }
}


export const getModelPriceExtremes = async (
    options: GetModelPriceExtremesParams = {}
): Promise<ModelPriceExtremeResponse> => {
    try {
        const {
            brand,
            fuel_type,
            body_type,
            transmission,
            drivetrain,
            is_popular = false,
            is_latest = false,
            is_upcoming = null,
            is_trending = false,
            seating_capacity,
            min_price,
            max_price,
        } = options;

        const params = new URLSearchParams();

        if (brand) params.set("brand", brand);
        if (is_popular) params.set("is_popular", "true");
        if (is_latest) params.set("is_latest", "true");
        if (is_upcoming === true) params.set("status", "upcoming");
        else if (is_upcoming === false) params.set("status", "active");
        if (is_trending) params.set("is_trending", "true");
        if (min_price) params.set("min_price", String(min_price));
        if (max_price) params.set("max_price", String(max_price));

        const addParam = (key: string, value?: string | string[]) => {
            if (!value) return;
            const values = Array.isArray(value) ? value : [value];
            values.forEach((v) => params.append(key, v.trim()));
        };

        addParam("fuel_type", fuel_type);
        addParam("body_type", body_type);
        addParam("transmission", transmission);
        addParam("drivetrain", drivetrain);
        addParam("seating_capacity", seating_capacity);

        const response = await httpClientV2(`model/price-extremes?${params.toString()}`);
        return {
            status: response?.status ?? "success",
            data: response?.data ?? {},
        } as ModelPriceExtremeResponse;
    } catch (error) {
        logger.error("Error fetching model price extremes:", error);
        return {
            status: "error",
            data: {}
        };
    }
};



//=============================================================
// =================== Poll APIs ==============================
//=============================================================

// Fetch variant list by model ID
export const getActivePoll = async () => {
    try {
        const response = await httpClientV2(`poll/active-poll`);
        if (response?.status === "error") return null;
        return response;
    } catch {
        // 404 is expected when no poll is active
        return null;
    }
}

// Fetch vote by poll ID API
export const getVoteByPollId = async (pollId: string) => {
    try {
        const url = `poll/${pollId}/get-poll-votes`;
        const response = await httpClientV2(url);
        return response;
    } catch (err) {
        logger.error("Error in getVoteByPollId:", err);
        throw err;
    }
}

// Save vote API
export const saveVote = async (pollId: string, option: string, sessionId?: string) => {
    try {
        const sessionId = getSessionId();
        const url = `poll/${pollId}/vote`;
        const body: { pollId: string; option: string; userId?: string } = { pollId, option };
        if (sessionId) {
            body.userId = sessionId;
        }
        const response = await httpClientV2(url, {
            method: "POST",
            body,
        });
        return response;
    }
    catch (err) {
        logger.error("Error in saveVote:", err);
        throw err;
    }
}

export async function getModelsGalleryByBodyTypeSlug({ slug, limit }: { slug: string; limit?: number }) {
    try {
        let url = `body-type/${encodeURIComponent(slug)}/models-gallery`

        if (limit !== undefined && limit > 0) {
            url += `?limit=${limit}`;
        }

        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getModelsGalleryByBodyTypeSlug:", err);
        return [];
    }
};


// Fetch all user reviews with optional filters (brand_id, model_id, review_type, limit, page)
export async function getAllUserReviews(options: {
    brand_id?: string;
    model_id?: string | string[];
    review_type?: string | string[];
    limit?: number;
    page?: number;
    bodytype?: string;
} = {}): Promise<{
    reviews: any[];
    error: string | null;
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    } | null;
}> {
    try {
        const params = new URLSearchParams();
        if (options.brand_id) params.append('brand_id', options.brand_id);
        if (options.model_id) {
            if (Array.isArray(options.model_id)) {
                params.append('model_id', options.model_id.join(','));
            } else {
                params.append('model_id', options.model_id);
            }
        }
        if (options.limit) params.append('limit', String(options.limit));
        if (options.page) params.append('page', String(options.page));
        if (options.review_type) {
            if (Array.isArray(options.review_type)) {
                params.append('review_type', options.review_type.join(','));
            } else {
                params.append('review_type', options.review_type);
            }
        }
        if (options.bodytype) params.append('body_type', options.bodytype);
        const url = `reviews/all-reviews${params.toString() ? `?${params.toString()}` : ''}`;
        const fetchedData = await httpClientV2(url);
        const data = fetchedData.data || [];
        const pagination = fetchedData.pagination || null;
        return { reviews: data, error: null, pagination };
    } catch (err: unknown) {
        logger.error("Error in getAllUserReviews:", err);
        const error =
            err instanceof Error
                ? err.message
                : "Failed to load user reviews. Please try again later.";
        return { reviews: [], error, pagination: null };
    }
}

export async function getFaqsByPage(page: string, pageType?: 'static' | 'dynamic') {
    try {
        let url = `faqs/by-page?pageIdentifier=${encodeURIComponent(page)}`;
        if (pageType) url += `&pageType=${pageType}`;
        const res = await httpClientV2(url);
        return res?.data ?? { faq: [] };
    } catch (err) {
        logger.error("Error in getFaqsByPage:", err);
        return { faq: [] };
    }
}

export async function getFaqsByReference(dynamicType: string, referenceId: string) {
    try {
        const params = new URLSearchParams({ dynamicType, referenceId });
        const res = await httpClientV2(`faqs/by-reference?${params.toString()}`);
        return res?.data || null;
    } catch (err) {
        logger.error("Error in getFaqsByReference:", err);
        return null;
    }
}

export async function getFaqsByTags(tags: string | string[]) {
    try {
        const tagStr = Array.isArray(tags) ? tags.join(',') : tags;
        const res = await httpClientV2(`faqs/by-tags?tags=${encodeURIComponent(tagStr)}`);
        return res?.data ?? [];
    } catch (err) {
        logger.error("Error in getFaqsByTags:", err);
        return [];
    }
}

// Search Cars and Brands
export async function searchCarsAndBrands(query: string) {
    if (!query || query.trim().length < 2) {
        return { brandsList: [], productList: [] };
    }
    try {
        const url = `general/search/${encodeURIComponent(query)}`;
        const data = await httpClientV2(url);
        return {
            brandsList: data?.data?.brands || [],
            productList: data?.data?.cars || [],
        };
    } catch (err) {
        logger.error("Error in searchCarsAndBrands:", err);
        return { brandsList: [], productList: [] };
    }
}

export async function getAllBodyTypes(): Promise<BodyType[]> {
    try {
        const url = `body-type/all`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getAllBodyTypes:", err);
        return [];
    }
}

export async function getBodyTypesWithModels(options: { is_upcoming?: boolean, limit?: number, sort?: string } = {}): Promise<any> {
    try {
        const { is_upcoming, limit, sort } = options;
        const params = new URLSearchParams();
        if (typeof is_upcoming === 'boolean') {
            params.set("is_upcoming", String(is_upcoming));
        }
        if (typeof limit !== 'undefined') {
            params.set("limit", String(limit));
        }
        if (typeof sort !== 'undefined') {
            params.set("sort", sort);
        }

        const url = `body-type/all-with-models${params.toString() ? `?${params.toString()}` : ''}`;
        const res = await httpClientV2(url);
        const data: any[] = res?.data || [];
        data.forEach(bt => { if (Array.isArray(bt.cars)) bt.cars = bt.cars.map(normalizePriceRange); });
        return data;
    } catch (err) {
        console.error("Error in getBodyTypesWithModels:", err);
        throw err;
    }
}

export async function getFuelTypesWithModels(options: { is_upcoming?: boolean, limit?: number, sort?: string } = {}): Promise<any> {
    try {
        const { is_upcoming, limit, sort } = options;
        const params = new URLSearchParams();
        if (typeof is_upcoming === 'boolean') {
            params.set("is_upcoming", String(is_upcoming));
        }
        if (typeof limit !== 'undefined') {
            params.set("limit", String(limit));
        }
        if (typeof sort !== 'undefined') {
            params.set("sort", sort);
        }

        const url = `fuel-type/all-with-models${params.toString() ? `?${params.toString()}` : ''}`;
        const res = await httpClientV2(url);
        const data: any[] = res?.data || [];
        data.forEach(ft => { if (Array.isArray(ft.cars)) ft.cars = ft.cars.map(normalizePriceRange); });
        return data;
    } catch (err) {
        console.error("Error in getFuelTypesWithModels:", err);
        throw err;
    }
}

export async function getBudgetsWithModels(options: { is_upcoming?: boolean, limit?: number, sort?: string } = {}): Promise<any> {
    try {
        const { is_upcoming, limit, sort } = options;
        const params = new URLSearchParams();
        if (typeof is_upcoming === 'boolean') {
            params.set("is_upcoming", String(is_upcoming));
        }
        if (typeof limit !== 'undefined') {
            params.set("limit", String(limit));
        }
        if (typeof sort !== 'undefined') {
            params.set("sort", sort);
        }

        const url = `budget/list${params.toString() ? `?${params.toString()}` : ''}`;
        const res = await httpClientV2(url);
        const data: any[] = res?.data || [];
        data.forEach(b => { if (Array.isArray(b.cars)) b.cars = b.cars.map(normalizePriceRange); });
        return data;
    } catch (err) {
        logger.error("Error in getBudgetsWithModels:", err);
        throw err;
    }
}

export async function getBrandBudgetSlabs(options: { brand: string; is_upcoming?: boolean }): Promise<any> {
    try {
        const { brand, is_upcoming } = options;
        // limit=1 — we only need to know which brand-scoped ranges have at
        // least one model, not the actual car listings. is_upcoming must
        // match BrandBudgetPage's own launched-models check, or a slug that
        // only has an upcoming model in that bracket gets listed as a chip
        // here and then 404s/redirects when the page verifies it.
        const params = new URLSearchParams({ brand, limit: "1" });
        if (typeof is_upcoming === "boolean") params.set("is_upcoming", String(is_upcoming));
        const url = `budget/list?${params.toString()}`;
        const res = await httpClientV2(url);
        return res;
    } catch (err) {
        logger.error("Error in getBrandBudgetSlabs:", err);
        throw err;

    }
}

export async function getBudgetRanges(): Promise<any> {
    try {
        const url = `budget/all`;
        const res = await httpClientV2(url);
        return res?.data || res || [];
    } catch (err) {
        logger.error("Error in getBudgetRanges:", err);
        return [];
    }
}

export async function getSeoMetaByPageSlug(page: string) {
    try {
        const params = new URLSearchParams();
        if (page) params.append('pageIdentifier', page);
        const url = `seo-meta/page?${params.toString()}`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getSeoMetaByPageSlug:", err);
        return [];
    }
}

export async function getBMSBannerByPlaceholder(
    screenType: 'desktop' | 'mobile',
    placeholderId: string
) {
    // Safe fallback banner (never return null/undefined)
    const FALLBACK = { campaigns: [] };

    if (!placeholderId) {
        return FALLBACK;
    }

    const { width, height } = bannerDimensions[screenType];
    const url = `${BMS_CONFIG.apiBaseUrl}/campaign/scheduled-campaign-banner/${placeholderId}?screenType=${screenType}&width=${width}&height=${height}&preview=false`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s hard timeout

    try {
        const res = await fetch(url, {
            headers: {
                'x-api-key': BMS_CONFIG.apiKey,
            },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!res.ok) {
            throw new Error(`Banner API status: ${res.status}`);
        }

        const data = await res.json();

        // Extra safety: ensure expected shape
        if (!data || !Array.isArray(data.campaigns)) {
            return FALLBACK;
        }

        return data;
    } catch (err: any) {
        clearTimeout(timeoutId);

        if (err.name === 'AbortError') {
            logger.error('Banner API timeout (5s)');
        } else {
            logger.error('Banner API failed:', err.message);
        }

        return FALLBACK;
    }
}

export async function trackBannerImpression(campaignId: string, platformId: string, brand: string, deviceType: string, placeholderId: string) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    try {
        const res = await fetch(
            `${BMS_CONFIG.apiBaseUrl}/banner/impression`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': BMS_CONFIG.apiKey
                },
                signal: controller.signal,
                body: JSON.stringify({
                    campaignId,
                    platformId,
                    brand,
                    deviceType,
                    state: '',
                    city: '',
                    language: 'en',
                    placeholderId
                }),
            }
        );
        clearTimeout(timeoutId);
        return res.json();
    } catch {
        clearTimeout(timeoutId);
        // Impression tracking is fire-and-forget — timeouts and failures are silently ignored
    }
}

export async function trackBannerClick(campaignId: string, platformId: string, brand: string, deviceType: string, placeholderId: string) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    try {
        const res = await fetch(
            `${BMS_CONFIG.apiBaseUrl}/banner/click`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': BMS_CONFIG.apiKey
                },
                signal: controller.signal,
                body: JSON.stringify({
                    campaignId,
                    platformId,
                    brand,
                    deviceType,
                    state: '',
                    city: '',
                    language: 'en',
                    placeholderId
                }),
            }
        );
        clearTimeout(timeoutId);
        return res.json();
    } catch {
        clearTimeout(timeoutId);
        // Click tracking is fire-and-forget — timeouts and failures are silently ignored
    }
}

// =============================================================
// =================== Submit Review Api =============================
// =============================================================
export async function submitReview(
    reviewData: {
        model_id: string;
        brand_id: string;
        reviewer_name: string;
        mobile_number: string;
        review_title: string;
        feedback: string;
        rating: number;
    }
) {
    try {
        const url = `reviews/create`;
        const res = await httpClientV2(url, {
            method: "POST",
            body: { ...reviewData, review_type: "Latest", status: "Pending" },
        });
        return res;
    } catch (error) {
        logger.error("Error in profile Update:", error);
        throw error;
    }
}

//=============================================================
// =================== Location APIs ==========================
//=============================================================

// Fetch cities from lm_cities_district_map via location/cities.
// CityQueryDto only accepts isPopular/limit/page — limit must be -1 (all)
// or a positive page size; the backend's global ValidationPipe rejects any
// other query param (forbidNonWhitelisted), so unsupported params must not
// be sent here.
export async function getCities(options: {
    limit?: number;
    page?: number;
    isPopular?: boolean;
} = {}) {
    try {
        const { limit = -1, page = 1, isPopular } = options;
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (isPopular) params.set("isPopular", "true");

        const url = `location/cities?${params.toString()}`;
        const res = await httpClientV2(url);
        // Response is always { data: [...] }, paginated or not
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getCities:", err);
        return [];
    }
}


// Fetch city by slug
async function _fetchCityBySlug(slug: string): Promise<CityData> {
    const url = `location/city/${encodeURIComponent(slug)}`;
    const res = await httpClientV2(url);
    return res?.data ? res.data : res;
}

export async function getCityBySlug(slug: string): Promise<CityData> {
    try {
        // Key includes `slug` — unstable_cache's automatic argument hashing
        // isn't relied on here (see getGlobalSettings/getNearestCities for
        // the same fix), so every city gets its own cache entry.
        return await unstable_cache(
            () => _fetchCityBySlug(slug),
            ['city-by-slug', slug],
            { revalidate: 3600 },
        )();
    } catch (err) {
        logger.error("Error in getCityBySlug:", err);
        throw err;
    }
}

// Fetch default city for user (used in location detection flow)
export async function getDefaultCity(): Promise<CityData | null> {
    try {
        const url = `location/city-default`;
        const res = await httpClientV2(url);
        const data = res?.data ? res.data : res;
        return {
            name: data?.name,
            slug: data?.slug,
            cityTier: data?.cityTier ?? null,
            state: {
                name: data?.state?.name,
                slug: data?.state?.slug,
                rtoSlabs: data?.state?.rtoSlabs || [],
                secondVehicleTaxPercent: data?.state?.secondVehicleTaxPercent || 0,
            },
            lat: data?.geo?.coordinates?.[1],
            long: data?.geo?.coordinates?.[0],
            priority: data?.priority || 'tier2',
        };
    } catch (err) {
        logger.error("Error in getDefaultCity:", err);
        return null;
    }
}

// Fetch default city for user with caching (used in location detection flow)
async function _fetchGlobalSettings(citySlug: string) {
    try {
        const url =
            citySlug !== ''
                ? `location/city/${encodeURIComponent(citySlug)}`
                : `location/city-default`;

        const res = await httpClientV2(url);
        const data = res?.data ? res.data : res;

        return {
            name: data?.name,
            slug: data?.slug,
            cityTier: data?.cityTier ?? null,
            state: {
                name: data?.state?.name,
                slug: data?.state?.slug,
                rtoSlabs: data?.state?.rtoSlabs || [],
                secondVehicleTaxPercent:
                    data?.state?.secondVehicleTaxPercent || 0,
            },
            lat: data?.geo?.coordinates?.[1],
            long: data?.geo?.coordinates?.[0],
            priority: data?.priority || 'tier2',
        };
    } catch (err) {
        logger.error("Error in getDefaultCity:", err);
        return null;
    }
}
export const getGlobalSettings = (citySlug = '') =>
    // Key includes `citySlug` so each city (and the no-slug default-city
    // case) gets its own cache entry instead of sharing one global slot.
    unstable_cache(
        () => _fetchGlobalSettings(citySlug),
        ['global-location-settings', citySlug],
        { revalidate: 3600 },
    )();

// Fetch default city for user with caching (used in location detection flow)
export const getDealerCityBySlug = (citySlug = '') => {
    const cachedFn = unstable_cache(
        async () => {
            try {
                const url =
                    citySlug !== ''
                        ? `location/dealer-city/${encodeURIComponent(citySlug)}`
                        : '';

                const res = await httpClientV2(url);
                const data = res?.data ? res.data : res;

                return {
                    name: data?.name,
                    slug: data?.slug,
                    cityTier: data?.cityTier ?? null,
                    state: {
                        name: data?.state?.name,
                        slug: data?.state?.slug,
                        code: data?.state?.code,
                        rtoSlabs: data?.state?.rtoSlabs || [],
                        secondVehicleTaxPercent:
                            data?.state?.secondVehicleTaxPercent || 0,
                    },
                    lat: data?.geo?.coordinates?.[1],
                    long: data?.geo?.coordinates?.[0],
                    priority: data?.priority || 'tier2',
                };
            } catch (err) {
                logger.error("Error in getDefaultCity:", err);
                return null;
            }
        },
        ["dealer-city-by-slug", citySlug],
        { revalidate: 3600 }
    );

    return cachedFn();
};

// Cached popular cities — on-road price calculated by frontend using returned state RTO data
const _getCachedPopularCities = unstable_cache(
    async (limit: number) => {
        try {
            const params = new URLSearchParams();
            if (limit > 0) params.set("limit", String(limit));
            const qs = params.toString();
            const url = `location/popular-cities${qs ? `?${qs}` : ""}`;
            const res = await httpClientV2(url);
            const data = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
            return data as any[];
        } catch (err) {
            console.error("Error in cached popular cities:", err);
            return [];
        }
    },
    ["popular-cities"],
    { revalidate: 60 * 60 * 6 } // 6 hours
);

export async function getPopularCities(opts: {
    lat?: number;
    lng?: number;
    limit?: number;
    slug?: string;
} = {}): Promise<any[]> {
    const { lat, lng, limit = 0, slug } = opts;

    if (lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng)) {
        try {
            const params = new URLSearchParams();
            if (limit > 0) params.set("limit", String(limit));
            params.set("lat", String(lat));
            params.set("lng", String(lng));
            if (slug) params.set("slug", slug);
            const res = await httpClientV2(`location/popular-cities?${params.toString()}`);
            return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching popular cities with exclusion:", err);
            return [];
        }
    }
    return _getCachedPopularCities(limit);
}

// Search cities by query string
export async function searchCities(query: string) {
    try {
        const url = `location/search?q=${encodeURIComponent(query)}`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in searchCities:", err);
        throw err;
    }
}

// Fetch nearest cities — on-road price calculated by frontend using returned state RTO data
export async function getNearestCities(payload: {
    lat?: number;
    long?: number;
    limit?: number;
    slug?: string;
    popular_cities?: boolean;
}): Promise<any[]> {
    const { long, lat, limit, slug, popular_cities } = payload;

    // Normalise lat/lng — be tolerant of swapped coordinates or missing values
    const rawLat = Number(lat ?? 0);
    const rawLng = Number(long ?? 0);
    const swapped = Math.abs(rawLat) > 90 && Math.abs(rawLng) <= 90;
    const normLat = swapped ? rawLng : rawLat;
    const normLng = swapped ? rawLat : rawLng;

    // if lat and lng are 0 then return empty array
    if (normLat === 0 && normLng === 0) return [];
    
    // add lat and lng to params
    const params: Record<string, any> = {};
    params.lat = normLat;
    params.lng = normLng;
    
    // if limit is 0 then return empty array
    if (limit) params.limit = limit;
    if (slug) params.slug = slug;
    if (popular_cities) params.popular_cities = true;

    const qs = new URLSearchParams(
        Object.entries(params).map(([k, v]) => [k, String(v)])
    ).toString();

    try {
        // Key includes the query string so distinct lat/lng/slug/limit
        // combinations don't collide on one cache entry, and the TTL matches
        // getGlobalSettings/getCityBySlug (1 hour) since all three read the
        // same underlying RTO slab data — a longer TTL here was letting this
        // widget serve stale RTO rates after the other two had refreshed.
        const result = await unstable_cache(
            () => _fetchNearestCities(qs),
            ["nearest-cities", qs],
            { revalidate: 3600 },
        )();
        return Array.isArray(result) ? result : [];
    } catch {
        return [];
    }
}

// Throws on failure so unstable_cache skips storing empty results
async function _fetchNearestCities(qs: string): Promise<any[]> {
    const url = `location/nearest-cities${qs ? `?${qs}` : ""}`;
    const res = await httpClientV2(url, { method: "GET" });
    const data = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : null);
    if (!data || data.length === 0) {
        throw new Error("nearest-cities: empty or error response");
    }
    return data as any[];
}

// Fetch location suggestions from Location Master
export async function searchLocations(
    opts: { q: string; page?: number; fuzzy?: boolean; lang?: string }
): Promise<any> {
    try {
        const { q, page = 1, fuzzy = true, lang = "english" } = opts;
        const params = new URLSearchParams({
            q,
            page: String(page),
            fuzzy: String(fuzzy),
            lang,
        });
        return await lmFetch(`search?${params.toString()}`);
    } catch (error) {
        logger.error("Error searching locations:", error);
        return { status: "error", data: [] };
    }
}

// Compute launch date filter options (quarterly buckets) — q2..q7 offset from current quarter
export const getLaunchFilters = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const currentQ = Math.floor(now.getMonth() / 3) + 1;

    const quarters = [];
    for (let n = 1; n <= 6; n++) {
        const totalQ = (currentQ - 1) + n;
        const q = (totalQ % 4) + 1;
        const y = year + Math.floor(totalQ / 4);
        quarters.push({ label: `Q${q} ${y}`, value: `q${n + 1}` });
    }
    return { data: quarters };
}

/**
 * Fetch all web stories with pagination and filters
 */
export const getAllWebStories = async (
    opts: {
        page?: number;
        limit?: number;
        search?: string;
        sort_by?: string;
    } = {}
): Promise<any> => {
    try {
        const { page = 1, limit = 10, search, sort_by } = opts;
        const params = new URLSearchParams({
            page: String(page),
            limit: String(limit),
        });

        if (search) params.set("search", search);
        if (sort_by) params.set("sort_by", sort_by);

        const url = `web-story/all?${params.toString()}`;
        const response = await httpClientV2(url);
        return response || { status: "error", data: [] };
    } catch (error) {
        logger.error("Error fetching web stories:", error);
        return { status: "error", data: [] };
    }
};

// fetch single web story data by slug
export const getWebStoryBySlug = async (slug: string) => {
    try {
        const url = `web-story/single/${slug}`;
        const response = await httpClientV2(url);
        return response || { status: "error", data: [] };
    } catch (error) {
        logger.error("Error fetching web story:", error);
        return { status: "error", data: [] };
    }
};

export async function getPageType(slug: string) {
    try {
        const url = `general/get-page-type/${encodeURIComponent(slug)}`;
        const res = await httpClientV2(url);
        return res?.data ? res.data.pageType : res;
    } catch (err) {
        logger.error("Error in getPageType:", err);
        return null;
    }
}

export async function getFilterOptions(opts: any) {
    const { fuel_type,
        body_type,
        transmission,
        drivetrain,
        seating_capacity,
        is_popular = false,
        is_latest = false,
        is_upcoming = undefined,
        is_trending = false,
        min_price,
        max_price,
        launch_date,
        is_new_launch = false,
        mileage,
        range,
        brand } = opts || {};
    try {
        const params = new URLSearchParams();
        if (is_latest) {
            params.append("is_latest", String(is_latest));
        }
        if (is_popular) {
            params.append("is_popular", String(is_popular));
        }
        if (brand) {
            params.append("brand", String(brand));
        }
        if (min_price) {
            params.set("min_price", String(min_price));
        }

        if (max_price) {
            params.set("max_price", String(max_price));
        }
        if (mileage) {
            params.set("mileage", String(mileage));
        }
        if (range) {
            params.set("range", String(range));
        }
        if (fuel_type) {
            params.set("fuel_type", String(fuel_type));
        }
        if (body_type) {
            params.set("body_type", String(body_type));
        }
        if (transmission) {
            params.set("transmission", String(transmission));
        }
        if (drivetrain) {
            params.set("drivetrain", String(drivetrain));
        }
        if (seating_capacity) {
            params.set("seating_capacity", String(seating_capacity));
        }
        if (is_upcoming === true) {
            params.set("status", "upcoming");
        } else if (is_upcoming === false) {
            params.set("status", "active");
        }
        if (is_trending) {
            params.set("is_trending", String(is_trending));
        }
        if (launch_date) {
            params.set("launch_date", String(launch_date));
        }
        if (is_new_launch) {
            params.set("is_new_launch", String(is_new_launch));
        }
        const url = `model/filters?${params.toString()}`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (error) {
        logger.error("Error fetching filter options:", error);
        return [];
    }
}
// Fetch published videos (paginated list)
export async function fetchAllVideos(opts: {
    page?: number;
    limit?: number;
    video_type?: string;
    channelId?: string;
    tags?: string;
    search?: string;
} = {}): Promise<{ videos: any[]; totalPages: number; totalCount: number; error: string | null }> {
    try {
        const { page = 1, limit = 20, video_type, channelId, tags, search } = opts;
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (video_type) params.set("video_type", video_type);
        if (channelId) params.set("channelId", channelId);
        if (tags) params.set("tags", tags);
        if (search) params.set("search", search);

        const res = await httpClientV2(`videos/all?${params.toString()}`);
        const pg = res?.pagination || {};
        return {
            videos: res?.data || [],
            totalPages: pg.totalPages || 1,
            totalCount: pg.total || res?.count || 0,
            error: null,
        };
    } catch (error) {
        logger.error("Error fetching videos:", error);
        return { videos: [], totalPages: 1, totalCount: 0, error: String(error) };
    }
}

// Fetch latest short and long videos in a single call (homepage/widgets).
// Accepts legacy positional call: getVideos(10) or getVideos(10, 'brand-slug,brand-slug-model-slug')
// Returns legacy-compatible shape: { data: { longVideos: { data }, shortVideos: { data } } }
export async function getVideos(
    limitOrOpts: number | { limit?: number; tags?: string; channelId?: string; video_type?: string } = 5,
    _legacyTags?: string,
): Promise<any> {
    try {
        const opts = typeof limitOrOpts === 'number'
            ? { limit: limitOrOpts, tags: _legacyTags }
            : limitOrOpts;
        const { limit = 5, tags, channelId } = opts;
        const params = new URLSearchParams({ limit: String(limit) });
        // When filtering by tags (e.g. a specific model), backfill with
        // untagged published videos if too few tagged ones exist, so the
        // section still fills out instead of showing just one card.
        if (tags) {
            params.set("tags", tags);
            params.set("fallback", "true");
        }
        if (channelId) params.set("channelId", channelId);

        const res = await httpClientV2(`videos/all-both-type?${params.toString()}`);
        // Return legacy-compatible shape so existing callers work without changes:
        // callers do: videosRes?.data?.longVideos?.data / videosRes?.data?.shortVideos?.data
        return {
            status: 'success',
            data: {
                longVideos: { data: res?.data?.longVideos?.data || [] },
                shortVideos: { data: res?.data?.shortVideos?.data || [] },
            },
        };
    } catch (error) {
        logger.error("Error fetching both-type videos:", error);
        return { status: 'error', data: { longVideos: { data: [] }, shortVideos: { data: [] } } };
    }
}

export async function meiliSearchApi(q: string) {
    try {
        const location = getLocationCookie();
        const citySlug = location?.slug ?? "";
        const url = `search/suggest?q=${encodeURIComponent(q)}&user_city=${encodeURIComponent(citySlug)}`;
        const res = await httpClientV2(url);
        return res || [];
    } catch (error) {
        console.error("Error fetching search results:", error, new Date().toISOString());
        return [];
    }
}

export async function getSponsoredHighlights() {
    try {
        const url = `search/sponsored/all`;
        const res = await httpClientV2(url);
        if (res && res.success) {
            return res.data || [];
        }
        return [];
    } catch (error) {
        console.error("Error fetching sponsored highlights:", error);
        return [];
    }
}

export async function getTrendingSearches() {
    try {
        const url = `search/trending/all`;
        const res = await httpClientV2(url);
        if (res && res.success) {
            return res.data || [];
        }
        return [];
    } catch (error) {
        console.error("Error fetching trending searches:", error);
        return [];
    }
}

// Fetch rating summary (average rating and total count) for a brand by slug
export async function getBrandRatingSummary(brandSlug: string) {
    try {
        const url = `reviews/rating-summary/${encodeURIComponent(brandSlug)}`;
        const fetchedData = await httpClientV2(url);
        return fetchedData?.data || { averageRating: 0, totalRatings: 0 };
    } catch (err: unknown) {
        logger.error("Error in getBrandRatingSummary:", err);
        return { averageRating: 0, totalRatings: 0 };
    }
}

// =============================================================
// =================== Author Page Api =========================
// =============================================================

// Fetch author list for author listing page
export const getAuthorList = async (options: { page?: number; limit?: number } = {}) => {
    try {
        const { page = 1, limit = -1 } = options;
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        const url = `authors/all?${params.toString()}`;
        const response = await httpClientV2(url);
        return response;
    } catch (error) {
        logger.error("Error fetching brands:", error);
        return { status: "error", data: [], totalCount: 0 };
    }
};

export const getSingleAuthorProfile = async (username: string) => {
    try {
        const url = `authors/single/${username}`;
        const response = await httpClientV2(url);
        // Check if response has valid data
        if (!response || !response.data || Object.keys(response.data).length === 0) {
            return { status: "error", data: null, notFound: true };
        }
        return response || { status: "error", data: null, notFound: true };
    } catch (error) {
        logger.error("Error fetching admin profile:", error);
        return { status: "error", data: null, notFound: true };
    }
};

// Fetch all author By news
export async function AuthorByNews(
    page: number = 1,
    limit: number = 9,
    date_filter: string = '',
    search: string = '',
    username: string = '',
): Promise<{ news: any[]; totalPages: number; totalCount: number; error: string | null }> {
    try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));

        if (search) {
            params.set("search", search);
        }

        if (date_filter) {
            params.set("date_filter", date_filter);
        }

        let url = `authors/news-by-author/${username}`;
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }

        const response = await httpClientV2(url);

        // Handle different response formats
        if (response && typeof response === 'object') {
            // If response has pagination structure
            if (response.status === 'success' && response.data) {
                return {
                    news: Array.isArray(response.data) ? response.data : [response.data],
                    totalPages: response.pagination?.totalPages || response.totalPages || 1,
                    totalCount: response.pagination?.total || response.totalCount || response.count || 0,
                    error: null
                };
            }
            // If it has a data property with array and pagination info
            else if (response.data && Array.isArray(response.data)) {
                return {
                    news: response.data,
                    totalPages: response.totalPages || Math.max(1, Math.ceil((response.totalCount || response.data.length) / limit)),
                    totalCount: response.totalCount || response.count || response.data.length,
                    error: null
                };
            }
            // If it's an array of objects
            else if (Array.isArray(response)) {
                return {
                    news: response,
                    totalPages: Math.max(1, Math.ceil(response.length / limit)),
                    totalCount: response.length,
                    error: null
                };
            }
        }

        // Default empty result
        return {
            news: [],
            totalPages: 1,
            totalCount: 0,
            error: null
        };
    } catch (err: unknown) {
        logger.error('Error fetching author news:', err);
        const error = err instanceof Error ? err.message : 'Failed to fetch author news';
        return { news: [], totalPages: 1, totalCount: 0, error };
    }
}

export interface GetDealersCitiesParams {
    page?: number;
    limit?: number;
    sort?: 'name_desc' | 'name_asc' | 'dealer_count_desc' | 'dealer_count_asc';
    brand_slug?: string;
    include_dealer_count?: boolean;
    is_popular?: boolean;
    city_slug?: string;
    exclude_city_slug?: string | string[];
}

export const getDealersCities = async (params: GetDealersCitiesParams = {}) => {
    try {
        const searchParams = new URLSearchParams();

        if (params.page) searchParams.append("page", String(params.page));
        if (params.limit) searchParams.append("limit", String(params.limit));
        if (params.sort) searchParams.append("sort", params.sort);
        if (params.brand_slug) searchParams.append("brand_slug", params.brand_slug);
        if (params.include_dealer_count !== undefined) {
            searchParams.append(
                "include_dealer_count",
                String(params.include_dealer_count)
            );
        }
        if (params.is_popular !== undefined) {
            searchParams.append("is_popular", String(params.is_popular));
        }
        if (params.city_slug !== undefined) {
            searchParams.append("city_slug", params.city_slug);
        }

        if (Array.isArray(params.exclude_city_slug)) {
            searchParams.append("exclude_city_slug", params.exclude_city_slug.join(","));
        } else if (params.exclude_city_slug) {
            searchParams.append("exclude_city_slug", params.exclude_city_slug);
        }

        const url = `dealers/cities${searchParams.toString() ? `?${searchParams.toString()}` : ""
            }`;

        const response = await httpClientV2(url);

        if (!response || !response.data) {
            return { status: "error", data: null };
        }

        return response;
    } catch (error) {
        logger.error("Error fetching dealer cities:", error);
        return { status: "error", data: null };
    }
};

export interface GetDealersBrandsParams {
    page?: number;
    limit?: number;
    sort?: 'name_desc' | 'name_asc' | 'dealer_count_desc' | 'dealer_count_asc';
    city_slug?: string;
    include_dealer_count?: boolean;
    brand_slug?: string;
    exclude_brand_slug?: string | string[];
}

export const getDealersBrands = async (params: GetDealersBrandsParams = {}) => {
    try {
        const searchParams = new URLSearchParams();

        if (params.page) searchParams.append("page", String(params.page));
        if (params.limit) searchParams.append("limit", String(params.limit));
        if (params.sort) searchParams.append("sort", params.sort);
        if (params.city_slug) searchParams.append("city_slug", params.city_slug);
        if (params.include_dealer_count !== undefined) {
            searchParams.append(
                "include_dealer_count",
                String(params.include_dealer_count)
            );
        }
        if (params.brand_slug) searchParams.append("brand_slug", params.brand_slug);

        if (Array.isArray(params.exclude_brand_slug)) {
            searchParams.append("exclude_brand_slug", params.exclude_brand_slug.join(","));
        } else if (params.exclude_brand_slug) {
            searchParams.append("exclude_brand_slug", params.exclude_brand_slug);
        }

        const url = `dealers/brands${searchParams.toString() ? `?${searchParams.toString()}` : ""
            }`;

        const response = await httpClientV2(url);

        if (!response || !response.data) {
            return { status: "error", data: null };
        }

        return response;
    } catch (error) {
        logger.error("Error fetching dealer brands:", error);
        return { status: "error", data: null };
    }
};

export interface GetDealersParams {
    page?: number;
    limit?: number;
    sort?: "asc" | "desc" | "name_asc" | "name_desc";
    brand_slug?: string;
    city_slug?: string;
    is_active?: boolean;
}

export const getDealers = async (params: GetDealersParams = {}) => {
    try {
        const searchParams = new URLSearchParams();

        if (params.page) searchParams.append("page", String(params.page));
        if (params.limit) searchParams.append("limit", String(params.limit));
        if (params.sort) searchParams.append("sort", params.sort);
        if (params.brand_slug) searchParams.append("brand_slug", params.brand_slug);
        if (params.city_slug) searchParams.append("city_slug", params.city_slug);

        if (params.is_active !== undefined) {
            searchParams.append(
                "is_active",
                String(params.is_active)
            );
        }

        const url = `dealers${searchParams.toString() ? `?${searchParams.toString()}` : ""
            }`;

        const response = await httpClientV2(url);

        if (!response || !response.data) {
            return { status: "error", data: null };
        }

        return response;
    } catch (error) {
        logger.error("Error fetching dealers:", error);
        return { status: "error", data: null };
    }
};

export interface GetNearbyDealerCitiesParams {
    lat: number;
    lng: number;
    slug?: string;
    brand_slug?: string;
    limit?: number;
    maxDistance?: number;
}

export const getNearbyDealerCities = async (
    params: GetNearbyDealerCitiesParams
) => {
    try {
        const cachedFn = unstable_cache(
            async () => {
                const searchParams = new URLSearchParams();

                searchParams.append("lat", String(params.lat));
                searchParams.append("lng", String(params.lng));

                if (params.slug) {
                    searchParams.append("slug", params.slug);
                }

                if (params.brand_slug) {
                    searchParams.append("brand_slug", params.brand_slug);
                }

                if (params.limit) {
                    searchParams.append("limit", String(params.limit));
                }

                if (params.maxDistance) {
                    searchParams.append(
                        "maxDistance",
                        String(params.maxDistance)
                    );
                }

                const url = `dealers/nearby-cities?${searchParams.toString()}`;
                const response = await httpClientV2(url);

                if (!response || !response.data || response.status === "error") {
                    throw new Error(
                        "nearby dealer cities: empty or error response"
                    );
                }

                return response;
            },
            [
                "nearby-dealer-cities",
                String(params.lat),
                String(params.lng),
                params.slug ?? "",
                params.brand_slug ?? "",
                String(params.limit ?? ""),
                String(params.maxDistance ?? ""),
            ],
            { revalidate: 60 * 60 * 6 } // 6 hours
        );

        return await cachedFn();
    } catch (error) {
        logger.error("Error fetching nearby dealer cities:", error);

        return {
            status: "error",
            data: null,
        };
    }
};

export interface CreateDealerLeadPayload {
    car_id: string;
    dealer: string;
    name: string;
    phone: string;
    location: string;
    locationData?: {
        type?: string;
        pincode?: string;
        cityId?: number;
        stateCode?: number | string;
        stateName?: string;
        stateSlug?: string;
        districtCode?: number | string;
        districtName?: string;
        districtSlug?: string;
        cityName?: string;
        citySlug?: string;
        areaName?: string;
        areaSlug?: string;
    };
    source?: string;
}

export const createDealerLead = async (
    payload: CreateDealerLeadPayload
) => {
    try {
        // The dealer lead endpoint validates locationData strictly and rejects
        // coordinates, which are only used to match dealers on regular leads.
        const { latitude, longitude, ...locationData } = (payload.locationData ?? {}) as Record<string, any>;
        const body = {
            ...payload,
            ...(payload.locationData ? { locationData } : {}),
            source: payload.source || "website"
        }
        const response = await httpClientV2("dealers/lead", {
            method: "POST",
            body,
        });

        if (!response || !response.data) {
            return {
                status: "error",
                data: null,
            };
        }

        return response;
    } catch (error) {
        logger.error("Error creating dealer lead:", error);

        return {
            status: "error",
            data: null,
        };
    }
};
export async function getAllTransmissions(): Promise<Transmission[]> {
    try {
        const url = `transmission/all`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getAllTransmissions:", err);
        return [];
    }
}

export async function getAllSeatingCapacities(): Promise<SeatingCapacity[]> {
    try {
        const url = `seating-capacity/all`;
        const res = await httpClientV2(url);
        return res?.data || [];
    } catch (err) {
        logger.error("Error in getAllSeatingCapacities:", err);
        return [];
    }
}

export async function getSeatingCapacityList(
    opts: GetSeatingCapacityListParams = {}
): Promise<FetchSeatingCapacityListResponse> {
    try {
        // limit defaults to 1 — callers only need each seating value's
        // label/slug to build chips, not its embedded cars list (the BFF
        // otherwise returns up to 5 cars per seating value).
        const { is_upcoming, sort, limit = 1, brand } = opts;
        const params = new URLSearchParams();
        if (typeof is_upcoming === 'boolean') {
            params.set("is_upcoming", String(is_upcoming));
        }
        if (sort) {
            params.set("sort", sort);
        }
        params.set("limit", String(limit));
        if (brand) {
            params.set("brand", brand);
        }
        const queryString = params.toString();
        const url = `seating-capacity/list${queryString ? `?${queryString}` : ""}`;
        const res = await httpClientV2(url);
        return res || { count: 0, data: [] };
    } catch (err) {
        logger.error("Error in getSeatingCapacityList:", err);
        return { count: 0, data: [] };
    }
}
export async function getModelSeriesByModelId(modelId: string): Promise<any> {
    try {
        if (!modelId) return null;
        const safeId = encodeURIComponent(String(modelId));
        const url = `model-series/${safeId}`;
        const res = await httpClientV2(url);
        return res?.data || res || null;
    } catch (err) {
        logger.error("Error in getModelSeriesByModelId:", err);
        return null;
    }
}

